import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, User, Users, Activity } from "lucide-react";
import PrintableToken from "@/components/PrintableToken";
import useSpeechSynthesis from "@/hooks/useSpeechSynthesis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TokenIssuer() {
  const [stats, setStats] = useState(null);
  const [recentTokens, setRecentTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [currentToken, setCurrentToken] = useState(null);
  const navigate = useNavigate();
  const { speakWithGenderVoices } = useSpeechSynthesis();

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
    }
  };

  const fetchRecentTokens = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/tokens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecentTokens(response.data.slice(-10).reverse());
    } catch (error) {
      console.error("Failed to fetch tokens", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRecentTokens();
    const interval = setInterval(() => {
      fetchStats();
      fetchRecentTokens();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleIssueToken = async (gender) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/tokens`,
        { gender },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const tokenData = response.data;
      
      toast.success(`Token ${tokenData.token_number} issued successfully!`, {
        description: `Gender: ${gender}`,
      });
      
      // Speak the token number in English with male and female voices
      const message = `Token ${tokenData.token_number} issued for ${gender} patient`;
      speakWithGenderVoices(message);
      
      // Show print modal
      setCurrentToken(tokenData);
      setShowPrintModal(true);
      
      fetchStats();
      fetchRecentTokens();
      
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi6Dzvjaj0APHGy98+CbTRAMUqvr8q5jHgU7n+n0xnksBSmAx/LZkEAKE1m48N2kUxQLSKXl8r5xIQYuf9T52ZBBChZmvvTinlEQC1Kp5O+wZB4EO5rj88h1KwUqfsXu1I4+CRJatPPepVYUC0me4fC+ciEGL4DN99mPQQoVZ77z4Z9RDwtRp+Lur2IdBTug6fTGeCsFKX/H8tiNQAoVYr/04p5SEAtPpe/us2YDBY6a4fLC');
      audio.play().catch(() => {});
    } catch (error) {
      toast.error("Failed to issue token");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all tokens? This will clear all token data for the day.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/tokens/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Reset response:", response.data);
      toast.success("All tokens reset successfully! Next token will be #1");
      fetchStats();
      fetchRecentTokens();
    } catch (error) {
      console.error("Reset error:", error);
      toast.error(error.response?.data?.detail || "Failed to reset tokens");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="token-issuer-page">
      {showPrintModal && currentToken && (
        <PrintableToken 
          tokenData={currentToken} 
          onClose={() => setShowPrintModal(false)} 
        />
      )}
      
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900">Token Issuer</h1>
            <p className="text-slate-600 mt-1">Richmond Medicare - Document Pre-check</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => window.open('/display', '_blank')}
              data-testid="open-display-button"
              className="h-10"
            >
              <Activity className="mr-2 h-4 w-4" />
              Open Display
            </Button>
            <Button
              variant="destructive"
              onClick={handleReset}
              data-testid="reset-button"
              className="h-10"
            >
              Reset Tokens
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button" className="h-10">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="hover-lift">
              <CardHeader className="pb-3">
                <CardDescription>Total Tokens</CardDescription>
                <CardTitle className="text-3xl">{stats.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="hover-lift">
              <CardHeader className="pb-3">
                <CardDescription>Issued</CardDescription>
                <CardTitle className="text-3xl text-slate-600">{stats.issued}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="hover-lift">
              <CardHeader className="pb-3">
                <CardDescription>In Progress</CardDescription>
                <CardTitle className="text-3xl text-blue-600">
                  {stats.at_registration + stats.at_physical_exam + stats.at_blood_collection + stats.at_xray}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card className="hover-lift">
              <CardHeader className="pb-3">
                <CardDescription>Completed</CardDescription>
                <CardTitle className="text-3xl text-emerald-600">{stats.completed}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Issue New Token</CardTitle>
            <CardDescription>Select gender and issue token after document pre-check</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-4">
            <Button
              onClick={() => handleIssueToken("Male")}
              disabled={loading}
              data-testid="issue-male-button"
              className="flex-1 h-24 text-xl font-semibold bg-indigo-600 hover:bg-indigo-700"
            >
              <User className="mr-3 h-8 w-8" />
              Issue Male Token
            </Button>
            <Button
              onClick={() => handleIssueToken("Female")}
              disabled={loading}
              data-testid="issue-female-button"
              className="flex-1 h-24 text-xl font-semibold bg-rose-600 hover:bg-rose-700"
            >
              <Users className="mr-3 h-8 w-8" />
              Issue Female Token
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Tokens</CardTitle>
            <CardDescription>Last 10 issued tokens</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="recent-tokens-list">
              {recentTokens.map((token) => (
                <div
                  key={token.token_number}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover-lift"
                  data-testid={`token-${token.token_number}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-2xl font-bold text-slate-900">{token.token_number}</span>
                    <Badge
                      className={token.gender === "Male" ? "bg-indigo-100 text-indigo-800" : "bg-rose-100 text-rose-800"}
                    >
                      {token.gender}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-900 capitalize">{token.status.replace("_", " ")}</div>
                    <div className="text-xs text-slate-500">{new Date(token.created_at).toLocaleTimeString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}